import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Dumbbell,
  Coffee,
  Sparkles,
  Users,
  Palette,
  Settings,
  LogOut,
  Moon,
  Sun,
  Sprout,
  Store,
  Mountain,
  Megaphone,
  BarChart3,
  Layers,
  ArrowLeft,
  Globe,
  Bean,
  Rocket,
  Radar,
  Workflow,
  Salad,
  Coffee as CoffeeIcon,
  Dumbbell as DumbbellIcon,
  Mountain as MountainIcon,
  ChevronDown,
  ChevronRight,
  Menu as MenuIcon,
  Building2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { AIAssistantDrawer } from "@/components/AIAssistantDrawer";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { PlanBanner } from "@/components/PlanBanner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SHELL } from "@/constants/testIds";
import { Sparkles as SparkleIcon } from "lucide-react";

const NAV = [
  { section: "Overview", items: [
    { key: "dashboard", to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  ]},
  { section: "Fitness", module: "fitness", items: [
    { key: "clients", to: "/app/fitness/clients", label: "Clients", icon: Users },
    { key: "workouts", to: "/app/fitness/workouts", label: "Workouts", icon: Dumbbell },
    { key: "exercises", to: "/app/fitness/exercises", label: "Exercise Library", icon: Layers },
    { key: "ai-workout", to: "/app/fitness/ai-workout", label: "AI Workout Generator", icon: Sparkles },
  ]},
  { section: "Café", module: "cafe", items: [
    { key: "pos", to: "/app/cafe/pos", label: "POS", icon: Coffee },
    { key: "menu", to: "/app/cafe/menu", label: "Menu", icon: Sprout },
    { key: "coffee-inventory", to: "/app/cafe/coffee-inventory", label: "Coffee Inventory", icon: Bean },
    { key: "kitchen", to: "/app/cafe/kitchen", label: "Kitchen Board", icon: BarChart3 },
  ]},
  { section: "Business", items: [
    { key: "ai", to: "/app/ai", label: "Business AI", icon: Sparkles, module: "ai" },
    { key: "retail", to: "/app/retail", label: "Retail Store", icon: Store, soon: true, module: "retail" },
    { key: "retreats", to: "/app/retreats", label: "Retreats", icon: Mountain, soon: true, module: "retreat" },
    { key: "marketing", to: "/app/marketing", label: "Marketing CRM", icon: Megaphone, module: "marketing" },
    { key: "growth", to: "/app/growth", label: "Growth Engine (B2B)", icon: Rocket, module: "growth" },
    { key: "automation", to: "/app/automation", label: "Automation", icon: Workflow, module: "automation" },
  ]},
  { section: "B2C Pipelines", module: "growth", collapsible: true, items: [
    { key: "b2c-intel",    to: "/app/b2c/intel",    label: "B2C Intelligence",  icon: Users },
    { key: "b2c-coffee",   to: "/app/b2c/coffee",   label: "B2C Coffee",        icon: CoffeeIcon },
    { key: "jaydiets",     to: "/app/jaydiets",     label: "B2C JAYDIETs",      icon: Salad },
    { key: "b2c-wellness", to: "/app/b2c/wellness", label: "B2C Wellness",      icon: DumbbellIcon },
    { key: "b2c-retreat",  to: "/app/b2c/retreat",  label: "B2C Retreat",       icon: MountainIcon },
  ]},
  { section: "Studio", items: [
    { key: "workspace", to: "/app/settings/workspace", label: "Workspace Overview", icon: Building2 },
    { key: "branding", to: "/app/settings/branding", label: "White Label", icon: Palette, module: "whitelabel" },
    { key: "domain", to: "/app/settings/domain", label: "Custom Domain", icon: Globe },
    { key: "team", to: "/app/settings/team", label: "Team & Roles", icon: Users },
    { key: "modules", to: "/app/settings/modules", label: "Modules", icon: Settings },
  ]},
  { section: "Platform", superAdminOnly: true, items: [
    { key: "admin", to: "/app/admin", label: "Parent Dashboard", icon: LayoutDashboard },
    { key: "laie", to: "/app/laie", label: "LAIE — Lead Engine", icon: Radar },
    { key: "crm", to: "/app/admin/crm", label: "Sales CRM", icon: Megaphone },
  ]},
];

/** Apply feature-flag gating: hide sections + items whose `module` key is not
 *  in the tenant's enabled `modules` list. Super-admin sees everything. */
function filterNav(user, tenant) {
  const enabled = new Set((tenant?.modules) || []);
  const isSuper = user?.role === "super_admin";
  return NAV
    .filter((s) => !s.superAdminOnly || isSuper)
    .filter((s) => !s.module || isSuper || enabled.has(s.module))
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => !it.module || isSuper || enabled.has(it.module)),
    }))
    .filter((s) => s.items.length > 0);
}

export function DashboardShell({ children }) {
  const { user, tenant, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Show "Back" whenever the user is deeper than /app root. `navigate(-1)`
  // uses browser history so it lands wherever they actually came from —
  // preferable to a hard-coded parent map because our nav is quite deep.
  const canGoBack = location.pathname !== "/app" && location.pathname !== "/app/";

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const filteredNav = filterNav(user, tenant);

  // Per-section collapse state — only for groups with `collapsible: true`.
  // Persisted to localStorage so the user's fold preference survives reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("kairosa.nav.collapsed") || "[]"));
    } catch { return new Set(); }
  });
  const toggleSection = (name) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem("kairosa.nav.collapsed", JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  const NavBody = ({ onNavigate }) => (
    <nav className="flex-1 overflow-y-auto px-3 pb-4">
      {filteredNav.map((group) => {
        const isCollapsed = group.collapsible && collapsed.has(group.section);
        return (
          <div key={group.section} className="mb-4">
            {group.collapsible ? (
              <button
                onClick={() => toggleSection(group.section)}
                data-testid={`nav-toggle-${group.section.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center gap-1 px-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
              >
                {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <span className="flex-1 text-left">{group.section}</span>
              </button>
            ) : (
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {group.section}
              </div>
            )}
            {!isCollapsed && (
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    data-testid={SHELL.navLink(item.key)}
                    className={({ isActive }) =>
                      [
                        "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-primary/10 text-foreground border-l-2 border-primary pl-[10px]"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted",
                      ].join(" ")
                    }
                  >
                    <item.icon className="h-4 w-4" strokeWidth={1.6} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.soon && (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">
                        soon
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const BrandBlock = (
    <div className="px-6 pt-6 pb-4">
      <Link to="/app" className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg leading-none">
          K
        </div>
        <div className="leading-tight">
          <div className="font-display text-lg">Kairosa</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Wellness OS
          </div>
        </div>
      </Link>
    </div>
  );

  const TenantBadge = (
    <div
      data-testid={SHELL.tenantSwitcher}
      className="mx-4 mb-4 rounded-lg border border-border bg-background/60 px-3 py-2.5"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        Workspace
      </div>
      <div className="mt-0.5 truncate text-sm font-medium">{tenant?.name || "—"}</div>
      <div className="text-xs text-muted-foreground capitalize">{tenant?.industry}</div>
    </div>
  );

  const UserFooter = (
    <div className="border-t border-border p-3 flex items-center gap-2">
      <div className="h-8 w-8 rounded-full bg-muted overflow-hidden grid place-items-center text-xs font-medium">
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
        ) : (
          (user?.name || "?").slice(0, 1)
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{user?.name}</div>
        <div className="truncate text-xs text-muted-foreground capitalize">
          {user?.role?.replace("_", " ")}
        </div>
      </div>
      <button
        data-testid={SHELL.themeToggle}
        onClick={toggle}
        aria-label="Toggle theme"
        className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
      <button
        data-testid={SHELL.logout}
        onClick={logout}
        aria-label="Log out"
        className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen text-foreground flex">
      {/* Desktop sidebar (>= lg) */}
      <aside
        data-testid={SHELL.sidebar}
        className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card/40"
      >
        {BrandBlock}
        {TenantBadge}
        <NavBody />
        {UserFooter}
      </aside>

      {/* Mobile drawer (< lg) */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="sr-only">
            <SheetTitle>Kairosa navigation</SheetTitle>
          </SheetHeader>
          {BrandBlock}
          {TenantBadge}
          <NavBody onNavigate={() => setMobileNavOpen(false)} />
          {UserFooter}
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-14 border-b border-border bg-background/70 backdrop-blur-xl px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button
              data-testid={SHELL.mobileMenuToggle}
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              className="lg:hidden rounded-md p-2 -ml-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
            {canGoBack && (
              <button
                data-testid="app-back-btn"
                onClick={() => {
                  // If there's no in-app history to pop back to, go to dashboard.
                  if (window.history.length > 1) navigate(-1);
                  else navigate("/app");
                }}
                aria-label="Go back"
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            <div className="lg:hidden font-display text-lg">Kairosa</div>
            <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-muted-foreground truncate">
              {tenant?.tagline || "One Platform. Infinite Wellness."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              data-testid="header-theme-toggle"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              <span className="font-mono uppercase tracking-wider text-[11px] hidden sm:inline">{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
            <CurrencySwitcher />
            {user?.role === "super_admin" && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/private/prd`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-download-prd"
                title="Download the private PRD PDF (super-admin only)"
                className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground rounded-full border border-border px-3 py-1.5 transition-colors"
              >
                PRD · PDF
              </a>
            )}
            <Link
              to="/"
              data-testid="back-to-site-app"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground rounded-full border border-border px-3 py-1.5 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
            <Button
              data-testid={SHELL.aiButton}
              onClick={() => setAiOpen(true)}
              size="sm"
              className="rounded-full gap-2"
            >
              <SparkleIcon className="h-4 w-4" />
              Ask Kairosa
            </Button>
          </div>
        </header>
        <PlanBanner />
        <main key={location.pathname} className="flex-1 animate-fade-up">
          {children}
        </main>
      </div>

      <AIAssistantDrawer open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
